import React, { useCallback, useContext, useRef, useState } from 'react';
import Spinner from '../Components/Spinner';
import axios from 'axios';
import { useAuth } from '../hooks/AuthProvider';
import { BASE_URL_API, STATIC_ICONS, defaultIcon } from '../Constants';
import DeleteIcon from '@mui/icons-material/Delete';
import { AddTemplateExample } from './AddTemplateExample';
import { useNavigate } from 'react-router-dom';
import '../Components/modal.css';
import './edit.css';
import { deleteWithIcon, deleteMultiWithIcon } from '../lib/actions';
import DataGridTable from '../Components/DataGridTable';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { LanguageContext } from '../context/LanguageContext';
import templateTranslations from '../languages/template.json';
import { getErrorMessage } from '../utils/errorUtils';
import AppDialog from '../Components/AppDialog';
import { normalizeTemplateExampleListRow } from '../utils/listRowIds';

export const TemplateExamples = () => {
  const { permissions } = useAuth();
  const canAdd = permissions['add_template_examples'];
  const canDelete = permissions['delete_template_examples'];
  const canView = canAdd || permissions['edit_template_examples'] || canDelete;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const navigate = useNavigate();
  const pagingoptions = [10, 20, 50, 100];

  const { lang } = useContext(LanguageContext);
  const t = templateTranslations[lang] || {};
  const isRtl = ['ar', 'ur'].includes(lang);

  const copy = {
    title: t.templateExamplesPageTitle || 'Template Examples',
    newItem: t.newTemplateExample || 'New Template Example',
    noData: t.noData || 'No available data.',
    noPermission: t.noPermission || "You don't have permission.",
    deleteLabel: t.delete || 'Delete',
    columns: {
      name: t.column?.name || 'Name',
      exampleName: t.column?.exampleName || 'Example Name',
      actions: t.column?.actions || 'Actions',
    },
  };

  const resolverRef = useRef(null);
  const [dialog, setDialog] = useState({
    open: false,
    type: 'alert',
    variant: 'info',
    icon: 'alert',
    title: '',
    message: '',
    confirmText: t.buttonOk || 'OK',
    cancelText: t.buttonCancel || 'Cancel',
    showCancel: false,
  });

  const closeDialog = useCallback((result = false) => {
    setDialog((d) => ({ ...d, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const alert = useCallback(
    (opts) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;
        setDialog({
          open: true,
          type: opts?.type || 'alert',
          variant: opts?.variant || 'info',
          icon: opts?.icon || 'alert',
          title: opts?.title || '',
          message: opts?.message || '',
          confirmText: opts?.confirmText || t.buttonOk || 'OK',
          cancelText: opts?.cancelText || t.buttonCancel || 'Cancel',
          showCancel: Boolean(opts?.showCancel),
        });
      }),
    [t.buttonCancel, t.buttonOk]
  );

  const showAlert = useCallback(
    async (type, message) => {
      const variant =
        type === 'error' ? 'error' : type === 'warning' ? 'info' : 'info';

      await alert({
        type: 'alert',
        variant,
        icon: 'alert',
        title:
          type === 'error'
            ? t?.alert?.errorTitle || 'Error'
            : type === 'warning'
            ? t?.alert?.warningTitle || 'Warning'
            : t?.alert?.infoTitle || 'Info',
        message: message || '',
        confirmText: t.buttonOk || 'OK',
      });
    },
    [alert, t?.alert?.errorTitle, t?.alert?.warningTitle, t?.alert?.infoTitle, t.buttonOk]
  );

  const handleRowClick = (params) => {
    navigate(`/configuration/template-examples/details/${params.row.id}`);
  };

  const baseColumns = [
    {
      field: 'icon',
      headerName: '',
      width: 60,
      align: 'center',
      renderCell: (params) =>
        params.row.icon ? (
          <img
            src={`${STATIC_ICONS}${params.row.icon}`}
            alt=""
            style={{
              width: 'calc(32px * var(--font-size-multiplier))',
              height: 'calc(32px * var(--font-size-multiplier))',
              marginTop: '8px',
              alignItems: 'center',
            }}
          />
        ) : (
          <img
            src={`${STATIC_ICONS}${defaultIcon}`}
            alt=""
            style={{
              width: 'calc(32px * var(--font-size-multiplier))',
              height: 'calc(32px * var(--font-size-multiplier))',
              marginTop: '8px',
              alignItems: 'center',
            }}
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        ),
    },
    {
      field: 'example_name',
      headerName: copy.columns.exampleName,
      width: 320,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <span
          style={{
            fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
          }}
        >
          {params.row.example_name || '-'}
        </span>
      ),
    },
    {
      field: 'name',
      headerName: copy.columns.name,
      width: 360,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <span
          style={{
            fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
          }}
        >
          {params.row.name || '-'}
        </span>
      ),
    },
  ];

  const fetchTemplateExamples = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      setRows([]);
      return;
    }

    setLoading(true);
    try {
      const result = await axios.get(`${BASE_URL_API}/template/examples/list`);
      setRows(
        Array.isArray(result.data?.data)
          ? result.data.data.map(normalizeTemplateExampleListRow)
          : []
      );
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      await showAlert('error', `${t.failedToLoadTemplateExamples || 'Failed to load template examples.'} ${errorMessage}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canView, showAlert, t.failedToLoadTemplateExamples]);

  React.useEffect(() => {
    fetchTemplateExamples();
  }, [fetchTemplateExamples]);

  const columns = canDelete
    ? [
        {
          field: 'actions',
          headerName: copy.columns.actions,
          type: 'actions',
          width: 100,
          getActions: (params) => [
            <GridActionsCellItem
              title={copy.deleteLabel}
              icon={
                <DeleteIcon
                  style={{
                    color: 'red',
                    fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))',
                  }}
                />
              }
              showInMenu
              label={copy.deleteLabel}
              onClick={() =>
                deleteWithIcon(
                  params.row.id,
                  params.row.icon,
                  'template/examples',
                  fetchTemplateExamples,
                  alert
                )
              }
            />,
          ],
        },
        ...baseColumns,
      ]
    : baseColumns;

  const [selectedRows, setSelectedRows] = React.useState([]);

  const handleMultiDelete = async (ids) => {
    if (!canDelete) {
      await showAlert('error', t.deleteTemplateExamplesPermissionError || 'You do not have permission to delete template examples.');
      return;
    }

    const icons = ids.map((id) => rows.find((row) => row.id === id)?.icon);
    await deleteMultiWithIcon(ids, icons, 'template/examples', fetchTemplateExamples, alert);
    setSelectedRows([]);
  };

  const selectActions = canDelete
    ? [
        {
          title: copy.deleteLabel,
          icon: (
            <DeleteIcon
              style={{
                color: 'rgb(236, 69, 78)',
                marginInlineEnd: '20px',
                fontSize: 'calc(var(--icon-size) * var(--font-size-multiplier))',
              }}
            />
          ),
          action: (ids) => handleMultiDelete(ids),
        },
      ]
    : [];

  if (!canView) return <div>{copy.noPermission}</div>;

  return (
    <div
      className="configuration devices-page"
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <AppDialog
        open={dialog.open}
        type={dialog.type}
        variant={dialog.variant}
        icon={dialog.icon}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        showCancel={dialog.showCancel}
        onConfirm={() => closeDialog(true)}
        onCancel={() => closeDialog(false)}
        onClose={() => closeDialog(false)}
      />

      {openAddModal ? (
        <AddTemplateExample
          setOpen={setOpenAddModal}
          refresh={fetchTemplateExamples}
        />
      ) : null}

      <div className="device-main">
        <div className="devices-header">
          <div className="devices-header-left">
            <h2 className="devices-title">{copy.title}</h2>
          </div>

          {canAdd ? (
            <button className="devices-primary-btn" onClick={() => setOpenAddModal(true)}>
              <span>{copy.newItem}</span>
            </button>
          ) : null}
        </div>

        {loading ? (
          <Spinner />
        ) : rows.length > 0 ? (
          <div
            className="table-wrapper"
            style={{
              flex: 1,
              minHeight: 0,
            }}
          >
            <DataGridTable
              columns={columns}
              rows={rows}
              pagingoptions={pagingoptions}
              idcolumn={'id'}
              handleRowClick={handleRowClick}
              checkboxSelection={canDelete}
              rowSelectionModel={selectedRows}
              setRowSelectionModel={setSelectedRows}
              selectActions={selectActions}
              showToolbar={true}
              tableKey="templateExamples"
              sx={{
                fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
              }}
            />
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
            }}
          >
            {copy.noData}
          </div>
        )}
      </div>
    </div>
  );
};
